import { memo } from "react";
import { useDispatch } from "react-redux";

import { useChartsContext } from "contexts/ChartContext";
import { BoxPlotDisplayMode } from "entities/charts/boxPlotDisplayMode";
import { updateChartByPath } from "store/features";

import { useChartDependencies, useChartEntities } from "hooks/charts";

import JitterPlot from "components/icons/JitterPlot";

import ToolbarButton from "../../base/ToolbarButton";
import { useBoxPlotModeToggle } from "./useBoxPlotModeToggle";

const JitterPlotToggle = () => {
  const dispatch = useDispatch();
  const { id, entityKind } = useChartsContext();
  const { jitterPlotToggle, chartTypeParam, xAxisFieldSync } = useChartEntities(id);
  const { groupBy } = useChartDependencies(entityKind);
  const isEnabled = jitterPlotToggle?.enabled;
  const isActive = jitterPlotToggle?.active;
  const showButton = jitterPlotToggle?.show;
  const toggleMode = useBoxPlotModeToggle(BoxPlotDisplayMode.Jitter);

  const toggle = (active: boolean) => {
    toggleMode(active);

    // Entering or leaving jitter reseeds the x-axis field from the group by — the field
    // is not remembered per mode. While the x-axis is synced to the focus field the axis
    // selector's sync effects own it, and this write replaces the whole x property, which
    // would drop the synced bin and race the session-storage restore.
    if (xAxisFieldSync?.active) {
      return;
    }

    dispatch(
      updateChartByPath({
        id,
        path: "features.chartTypeParam.properties.x",
        value: groupBy
          ? {
              canBin: groupBy?.canBin,
              dataType: groupBy?.dataType,
              id: "",
              property: groupBy?.property,
              title: groupBy?.title,
              defaultUnit: groupBy?.defaultUnit
            }
          : { ...chartTypeParam.properties.x }
      })
    );
  };

  if (!showButton) {
    return null;
  }

  return (
    <ToolbarButton
      data-testid="jitter-plot-toggle"
      icon={<JitterPlot />}
      active={isActive}
      overflowLabel="Show Jitter Plots"
      tooltipText="Toggle Jitter Plot"
      onToggle={toggle}
      disabled={!isEnabled}
    />
  );
};

export default memo(JitterPlotToggle);
